import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import DashboardPageHeader from '../components/DashboardPageHeader';
import ProfileSettings from '../components/ProfileSettings';
import UnifiedLoader from '../components/UnifiedLoader';
import { ArrowLeftIcon as ArrowLeft, ShieldIcon as Shield, SparklesIcon } from '../components/Icons';

const UserDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { API_URL, user: authUser } = useContext(AuthContext);
    const { profile: myProfile, refreshProfile, loadingProfile } = useOutletContext();
    
    const [targetUser, setTargetUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTargetUser = async () => {
            // Case 1: Viewing own profile (no ID or ID is own ID)
            if (!id || (myProfile && id === myProfile._id)) {
                setTargetUser(myProfile);
                setLoading(loadingProfile);
                return;
            }

            // Case 2: Viewing someone else's profile
            try {
                setLoading(true);
                const config = {
                    headers: { Authorization: `Bearer ${authUser.token}` },
                };
                const { data } = await axios.get(`${API_URL}/users/${id}`, config);
                setTargetUser(data);
                setError(null);
            } catch (err) {
                console.error('Fehler beim Laden des Benutzers:', err);
                setError('Benutzer konnte nicht geladen werden.');
            } finally {
                setLoading(false);
            }
        };

        if (authUser) {
            fetchTargetUser();
        }
    }, [id, myProfile, authUser, API_URL, loadingProfile]);

    if (loading || loadingProfile) return <UnifiedLoader fullscreen />;

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
                <p className="text-error font-bold mb-4">{error}</p>
                <button onClick={() => navigate('/users')} className="md3-btn-tonal">
                    Zurück zur Liste
                </button>
            </div>
        );
    }

    if (!targetUser) return null;

    const isOwnProfile = !id || targetUser._id === authUser._id;
    const isAdminView = !isOwnProfile;

    const profilePhoto = targetUser.userData?.profilePhoto?.url || targetUser.userData?.photos?.[0]?.url;
    const avatarUrl = targetUser.avatarUrl || targetUser.userData?.avatarConfig?.modelUrl;
    const displayImage = profilePhoto || avatarUrl;


    return (
        <div className="max-w-4xl mx-auto px-6 py-8 md:py-12 animate-fade-in">
            <DashboardPageHeader 
                title={isOwnProfile ? "Mein Account" : (targetUser.userData?.scoutName || `${targetUser.userData?.firstName || ''} ${targetUser.userData?.lastName || ''}`.trim() || targetUser.email)}
                description={isOwnProfile ? "Verwalte deine organisatorischen Daten und Kontoeinstellungen." : `Benutzerprofil von ${targetUser.email}`}
                image={displayImage}
                backButton={
                    isAdminView ? (
                        <button
                            onClick={() => navigate('/users')}
                            className="w-12 h-12 rounded-full flex items-center justify-center bg-surface-containerHigh text-surface-on hover:bg-primary hover:text-primary-on transition-all shadow-sm shrink-0"
                            title="Zurück zur Liste"
                        >
                            <ArrowLeft sx={{ fontSize: 24 }} />
                        </button>
                    ) : null
                }
                actions={
                    <div className="flex items-center gap-2">
                        {isAdminView && (targetUser.role === 'admin' || targetUser.role === 'gruppenleiter' || targetUser.userData?.isTeamMember) && (
                            <button
                                onClick={() => navigate(`/edit/t/${targetUser._id}`)}
                                className="px-3.5 py-2 rounded-2xl bg-primary/10 hover:bg-primary/20 text-primary text-label-medium font-bold flex items-center gap-1.5 transition-colors border border-primary/20"
                                title="Team-Profil & Sichtbarkeit bearbeiten"
                            >
                                <SparklesIcon size={16} />
                                <span>Team-Profil</span>
                            </button>
                        )}
                        <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-2xl bg-surface-containerHigh text-surface-onVariant text-label-medium font-bold">
                            <Shield size={14} />
                            <span>{(targetUser.role || '').toUpperCase()}</span>
                        </div>
                    </div>
                }
            />

            <div className="space-y-8">
                <ProfileSettings 
                    profile={targetUser} 
                    isAdminView={isAdminView} 
                    onUpdate={(updatedData) => {
                        setTargetUser(updatedData);
                        if (isOwnProfile) refreshProfile();
                    }}
                />
            </div>
        </div>
    );
};

export default UserDetailPage;
